'use client';

import React from 'react';
import { DateTime } from 'luxon';
import { PiClock, PiTagSimple } from 'react-icons/pi';
import EventDTO from '../../models/event';
import EventBannerImage from '../atoms/EventBannerImage/EventBannerImage';
import useIsBackoffice from '../../hooks/useIsBackoffice';
import usePrefetchOnIntent from '../../hooks/usePrefetchOnIntent';

const AR_TIMEZONE = 'America/Argentina/Buenos_Aires';
const FALLBACK_BANNER = '/favicon.png';
// "Esta semana" cards are horizontally scrolled at 80% / 45% / 31% / 24% of viewport.
const WEEK_CARD_IMAGE_SIZES = '(min-width: 1280px) 24vw, (min-width: 1024px) 31vw, (min-width: 640px) 45vw, 80vw';

interface WeekEventCardProps {
  event: EventDTO;
  onSelect: (eventId: string) => void;
  /**
   * Public category to badge on the image (slug + display name). Callers pass
   * the first entry of the event's `categories`; events with none assigned
   * leave it `undefined` and the chip is omitted.
   */
  category?: { slug: string; name: string };
  /** Pre-rendered category icon (matches the slug). Optional. */
  categoryIcon?: React.ReactNode;
}

const WeekEventCard: React.FC<WeekEventCardProps> = ({
  event, onSelect, category, categoryIcon,
}) => {
  const isBackoffice = useIsBackoffice();

  const dt = DateTime.fromISO(event.date, { zone: AR_TIMEZONE }).setLocale('es');
  const dow = dt.toFormat('ccc').replace('.', '').slice(0, 3).toUpperCase(); // "JUE"
  const dayNumber = dt.day; // 15
  const time = dt.toFormat('HH:mm'); // "21:00"

  const eventHref = isBackoffice
    ? `/admin/${event.business_id}/events/${event.id}`
    : `/events/${event.slug}`;
  const prefetchHandlers = usePrefetchOnIntent(eventHref, !isBackoffice);

  return (
    <a
      href={eventHref}
      onClick={(e) => {
        e.preventDefault();
        onSelect(isBackoffice ? event.id : event.slug);
      }}
      {...prefetchHandlers}
      className="group flex flex-col w-full rounded-3xl border border-border-secondary bg-surface-base landing-event-card-shadow overflow-hidden cursor-pointer no-underline text-inherit transition-transform duration-200 hover:-translate-y-1"
    >
      <div className="relative w-full overflow-hidden aspect-[1920/700] bg-surface-secondary">
        <EventBannerImage
          src={event.banner_image || FALLBACK_BANNER}
          alt={`${event.name} - ${event.business_name}`}
          sizes={WEEK_CARD_IMAGE_SIZES}
          loading="lazy"
          className="object-cover group-hover:scale-105 transition-transform duration-300"
        />

        {/* Date chip — top-left */}
        <span
          className="absolute top-2.5 left-2.5 flex flex-col items-center justify-center min-w-10 px-2 py-1 rounded-xl bg-white/95 shadow-sm leading-none"
          aria-label={`${dow} ${dayNumber}`}
        >
          <span className="text-[10px] font-bold text-primary-500 uppercase tracking-wider">
            {dow}
          </span>
          <span className="text-base font-extrabold text-neutral-900 mt-0.5">
            {dayNumber}
          </span>
        </span>

        {/* Category chip — bottom-left (shown only when the BE returns a category) */}
        {category && (
          <span className="absolute bottom-2.5 left-2.5 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/95 shadow-sm text-xs-semibold text-black">
            {categoryIcon ?? <PiTagSimple className="text-sm" />}
            {category.name}
          </span>
        )}
      </div>

      <div className="flex flex-col gap-1 px-4 pt-3 pb-4">
        <h3 className="text-md-bold text-text-primary leading-tight line-clamp-2 [display:-webkit-box] [-webkit-box-orient:vertical] m-0">
          {event.name}
        </h3>
        <p className="flex items-center gap-1.5 text-xs-medium text-text-secondary m-0">
          <PiClock className="text-primary-500 text-sm flex-shrink-0" />
          <span>{time} hs</span>
          <span className="mx-0.5" aria-hidden="true">·</span>
          <span className="truncate">{event.place}</span>
        </p>
      </div>
    </a>
  );
};

export default WeekEventCard;
